// frontend/src/components/PulmonaryAddModal.js
// Modal component for adding pulmonary data directly in the flowsheet

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Input,
  Space,
  Typography,
  Alert,
  message,
  Divider
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { usePatientChart } from '../context/PatientChartContext';
import MomentDatePicker from './MomentDatePicker.tsx';

const { Title, Text } = Typography;

/**
 * PulmonaryAddModal Component
 * Provides a dedicated interface for adding pulmonary data to the flowsheet
 */
const PulmonaryAddModal = ({
  visible,
  onClose,
  onSave,
  patientId,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(moment());
  const { refreshVitals } = usePatientChart();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedDateTime(moment());
    }
  }, [visible, form]);

  // Handle form submission
  const handleSave = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form fields
      const values = await form.validateFields();
      
      // Filter out empty values
      const filteredValues = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
          filteredValues[key] = values[key];
        }
      });

      if (Object.keys(filteredValues).length === 0) {
        message.warning('Please enter at least one pulmonary parameter.');
        return;
      }

      // Prepare payload for backend - using the biometrics endpoint
      const payload = {
        entryDate: selectedDateTime.toISOString(),
        biometrics: filteredValues
      };

      // Call backend API using the existing biometrics endpoint
      const response = await axios.post(`/patients/${patientId}/flowsheet/biometrics`, payload);

      if (response.data.status === 'success') {
        message.success('Pulmonary data saved successfully!');

        // Refresh vitals banner (in case pulmonary data affects displayed metrics)
        refreshVitals();

        // Call parent onSave callback to refresh flowsheet
        if (onSave) {
          onSave();
        }

        // Close modal and reset form
        onClose();
        form.resetFields();
      } else {
        throw new Error(response.data.message || 'Failed to save pulmonary data');
      }
    } catch (error) {
      console.error('Error saving pulmonary data:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to save pulmonary data');
    } finally {
      setSubmitLoading(false);
    }
  };

  // No need for field arrays - we'll use the same structure as PulmonaryInputForm

  return (
    <Modal
      title={
        <Space>
          <ClockCircleOutlined />
          Add Pulmonary Data
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Add New Pulmonary Entry"
          description="Enter pulmonary function test results and respiratory measurements for the selected date and time. These assessments help track lung function and respiratory health changes over time."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {/* Date/Time Selection */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Text strong>Entry Date & Time:</Text>
            <br />
            <MomentDatePicker
              showTime
              value={selectedDateTime}
              onChange={setSelectedDateTime}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select date and time"
            />
          </Col>
        </Row>
      </div>

      <Form form={form} layout="vertical" name="pulmonary_add_form">
        <Title level={4}>Enter Pulmonary Data</Title>
        <Alert
          message="Enter Spirometry Measurement Results"
          description="Record spirometry values including predicted values, actual measurements, and additional parameters. Predicted values help establish baseline expectations for comparison."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* First Row: FEV1 Predicted and FEV1 */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="biometrics_pulmonary_fev1_predicted"
              label="FEV1 Predicted (L)"
              rules={[{ type: 'number', min: 0, message: 'Value cannot be negative' }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Enter predicted FEV1" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="biometrics_pulmonary_fev1"
              label="FEV1 (L)"
              rules={[{ type: 'number', min: 0, message: 'Value cannot be negative' }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Enter actual FEV1" />
            </Form.Item>
          </Col>
        </Row>

        {/* Second Row: FVC Predicted and FVC */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="biometrics_pulmonary_fvc_predicted"
              label="FVC Predicted (L)"
              rules={[{ type: 'number', min: 0, message: 'Value cannot be negative' }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Enter predicted FVC" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="biometrics_pulmonary_fvc"
              label="FVC (L)"
              rules={[{ type: 'number', min: 0, message: 'Value cannot be negative' }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Enter actual FVC" />
            </Form.Item>
          </Col>
        </Row>

        {/* Third Row: PEFR Predicted and PEFR */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="biometrics_pulmonary_pefr_predicted"
              label="PEFR Predicted (L/min)"
              rules={[{ type: 'number', min: 0, message: 'Value cannot be negative' }]}
            >
              <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="Enter predicted PEFR" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="biometrics_pulmonary_pefr"
              label="PEFR (L/min)"
              rules={[{ type: 'number', min: 0, message: 'Value cannot be negative' }]}
            >
              <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="Enter actual PEFR" />
            </Form.Item>
          </Col>
        </Row>

        {/* Fourth Row: FEF 25-75% and FEV1/FVC Ratio */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="biometrics_pulmonary_fef25_75"
              label="FEF 25-75% (L/s)"
              rules={[{ type: 'number', min: 0, message: 'Value cannot be negative' }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Enter FEF 25-75%" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="biometrics_pulmonary_fev1_fvc_ratio"
              label="FEV1/FVC Ratio"
              rules={[{ type: 'number', min: 0, max: 1, message: 'Ratio must be between 0 and 1' }]}
            >
              <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} placeholder="e.g., 0.75" />
            </Form.Item>
          </Col>
        </Row>

        {/* Fifth Row: Lung Age Estimate */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="biometrics_pulmonary_lung_age_estimate"
              label="Lung Age Estimate (years)"
              rules={[{ type: 'number', min: 0, message: 'Value cannot be negative' }]}
            >
              <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="Enter lung age estimate" />
            </Form.Item>
          </Col>
        </Row>

        {/* Additional Information */}
        <Divider orientation="left">Test Instructions</Divider>
        <Alert
          message="Pulmonary Function Test Guidelines"
          description={
            <div>
              <p><strong>Spirometry Tests:</strong> Ensure patient performs maximum effort with proper technique. Record best of three acceptable maneuvers.</p>
              <p><strong>FEV₁ (Forced Expiratory Volume):</strong> Volume exhaled in first second of forced expiration.</p>
              <p><strong>FVC (Forced Vital Capacity):</strong> Total volume exhaled during forced expiration.</p>
              <p><strong>PEFR (Peak Expiratory Flow Rate):</strong> Maximum flow rate achieved during forced expiration.</p>
              <p><strong>Predicted Values:</strong> Expected normal values based on age, height, sex, and ethnicity for comparison with actual measurements.</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item style={{ textAlign: 'right', marginTop: 24 }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSave} loading={submitLoading}>
            Save Pulmonary Data
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PulmonaryAddModal;
